import { type BibleViewOptions, type BiblePassageContent, type BiblePassageData } from '@soulhx/fs-common';
import { type FootnoteData } from '../dm/bible-types.js';
import { nextAlpha, nextUpperAlpha } from './utilities.js';
import { contentParaTypes } from './constants.js';

export interface ParallelPassage {
  content: BiblePassageContent[];
  copyright?: string;
  footnotes: FootnoteData[];
  crossReferences: FootnoteData[];
}

export interface RefactorContentResponse {
  content: BiblePassageContent[];
  footnotes: FootnoteData[];
  crossReferences: FootnoteData[];
  parallels?: ParallelPassage[];
}

export const refactorContent = (
  options: BibleViewOptions,
  initialList?: BiblePassageContent[],
  parallels?: BiblePassageData[]
): RefactorContentResponse => {
  if (!initialList || initialList.length < 1) return { content: [], footnotes: [], crossReferences: [] };

  let nextFnID: string | undefined = undefined;
  let nextXrefId: string | undefined = undefined;
  const footnotes: FootnoteData[] = [];
  const crossReferences: FootnoteData[] = [];

  const recursivelyIterate = (list?: BiblePassageContent[]) => {
    if (!list || !Array.isArray(list)) return [];

    const result: BiblePassageContent[] = [];
    let currentChapter: BiblePassageContent | undefined = undefined;

    for (const item of list) {
      if (item.type === 'tag' && item.name === 'chapter') {
        currentChapter = item;
        continue;
      }

      if (item.type === 'tag' && item.name === 'note') {
        if (!options.footnotes) continue;

        // cross-refs
        if (item.attrs?.style === 'x' || item.attrs?.style === 'ex') {
          if (!options.crossRef) continue;

          nextXrefId = nextUpperAlpha(nextXrefId);
          crossReferences.push({ fnIdentifier: nextXrefId, fn: item });
          result.push({
            type: 'tag',
            name: 'fnID',
            attrs: { style: 'fnID' },
            items: [{ type: 'text', text: `[${nextXrefId}]` }],
          });
          continue;
        }

        nextFnID = nextAlpha(nextFnID);
        footnotes.push({ fnIdentifier: nextFnID, fn: item });
        result.push({
          type: 'tag',
          name: 'fnID',
          attrs: { style: 'fnID' },
          items: [{ type: 'text', text: `[${nextFnID}]` }],
        });
        continue;
      }

      if (
        currentChapter &&
        item.type === 'tag' &&
        item.name === 'para' &&
        item.attrs?.style &&
        contentParaTypes.includes(item.attrs.style)
      ) {
        const newItem: BiblePassageContent = {
          ...item,
          items: [currentChapter, ...recursivelyIterate(item.items)],
          attrs: item.attrs,
        };
        currentChapter = undefined;
        result.push(newItem);
        continue;
      }

      if (item.items) {
        result.push({ ...item, items: recursivelyIterate(item.items) });
      } else {
        result.push(item);
      }
    }

    return result;
  };

  const newItemList = recursivelyIterate(initialList);

  let responseParallels: ParallelPassage[] | undefined;
  if (parallels && parallels.length > 0) {
    responseParallels = parallels.map((p) => {
      const newParallel = refactorContent(options, p.content);

      return {
        content: newParallel.content,
        copyright: p.copyright,
        footnotes: newParallel.footnotes,
        crossReferences: newParallel.crossReferences,
      };
    });
  }

  return { content: newItemList, footnotes, parallels: responseParallels, crossReferences };
};
